'use client'
import { RootState } from '@/app/globalRedux/store'
import { useTranslation } from '@/app/i18n/client'
import { Contact } from '@/app/types/ContactType'
import { ComapnyContactUsResponse } from '@/app/types/contactUsType'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { companyContactService } from '@/services/contactUsService'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Autocomplete, TextField } from '@mui/material'
import { useParams } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import sanitizeHtml from 'sanitize-html'
import { gtmContactUsFormSubmission } from '../../GTMTrackers/GTMCompanyTracker'
import { formateContactsList } from '@/app/utils/formatListData'
import HCaptcha from '@hcaptcha/react-hcaptcha'
import ReCAPTCHA from 'react-google-recaptcha'
import { CaptchaValidator } from '@/app/hooks/useCaptcha'
import FeatureFlagCaptcha from '@/app/[lng]/components/Validation/FeatureFlagCaptcha'

const ContactUsForm = ({
  id,
  company_id,
  contactMembers,
  latitude,
  longitude,
}: {
  id: string
  company_id: string
  contactMembers: Contact[]
  latitude: number | null
  longitude: number | null
}) => {
  const captchaRef = useRef<HCaptcha | ReCAPTCHA | null>(null)
  const { validateCaptcha } = CaptchaValidator(captchaRef)

  const user = useSelector((state: RootState) => state.user)
  const [name, setName] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [message, setMessage] = useState<string>('')
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false)
  const { t } = useTranslation()
  const params = useParams()
  const handleSubmit = async () => {
    setFormSubmitted(true)
    if (name.length <= 0 || email.length <= 0 || message.length <= 0) {
      return
    }
    const isCaptchaVerified = await validateCaptcha()
    if (!isCaptchaVerified) return
    const formData = new FormData()
    formData.append('company_lead[name]', name)
    formData.append('company_lead[email]', email)
    formData.append('company_lead[message]', sanitizeHtml(message))
    const response: ComapnyContactUsResponse = await companyContactService(
      formData,
      id
    )
    if (response.status == 'Success') {
      gtmContactUsFormSubmission(String(params?.id) ?? '', company_id)
      setName('')
      setEmail('')
      setMessage('')
      setFormSubmitted(false)
    }
  }

  useEffect(() => {
    setEmail(user?.loggedIn ? user?.userEmail : '')
  }, [user.loggedIn])

  return (
    <div className="grid grid-cols-7 bg-[#fafafa] shadow-[0_0_9px_0_rgba(0,0,0,0.14)] p-[1px] rounded-[5px] mobile:grid-cols-1">
      <div className="col-span-4 px-10 py-11 gap-6 flex flex-col mobile:p-[15px]">
        {/* Form Heading */}
        <div className="flex flex-col gap-3">
          <h2 className="text-[22px] font-ns_bold leading-normal tracking-normal">
            {t('Send us a message')}
          </h2>
          <p className="text-sm leading-normal tracking-normal text-[#717171]">
            {t('Contact us form')}
          </p>
        </div>
        {/* Form Field */}
        <div className="flex flex-col gap-[18px]">
          {/* Name */}
          <div className="flex flex-col gap-2 relative">
            <Label htmlFor="name">{t('Company Member')} *</Label>
            <Autocomplete
              disablePortal
              disableClearable
              className="w-full bg-[white]"
              options={
                contactMembers?.map((member) => formateContactsList(member)) ??
                []
              }
              renderInput={(params) => (
                <TextField {...params} placeholder={t('Select a member')} />
              )}
              value={name}
              popupIcon={
                <ExpandMoreIcon
                  className="text-expand_icon"
                  style={{ width: '20px', height: '20px' }}
                />
              }
              onChange={(event, value) => {
                setName(value.split(' - ')[0])
              }}
            />
            <div className="absolute -bottom-4 text-red text-[10px]">
              {formSubmitted && name.length <= 0 && (
                <div className="text-required_color">
                  {t('Please select a member')}
                </div>
              )}
            </div>
          </div>
          {/* Email */}
          <div className="flex flex-col gap-2 relative">
            <Label htmlFor="email">{t('Email')} *</Label>
            <Input
              id="email"
              placeholder={t('Enter Email')}
              type="text"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
              }}
            />
            <div className="absolute -bottom-4 text-red text-[10px]">
              {formSubmitted && email.length <= 0 && (
                <div className="text-required_color">
                  {t('Please enter your email')}
                </div>
              )}
            </div>
          </div>
          {/* Message */}
          <div className="flex flex-col gap-2 relative">
            <Label htmlFor="message">{t('Message')} *</Label>
            <Textarea
              id="message"
              className="h-[100px] text-sm"
              maxLength={1000}
              placeholder={t('Enter the message')}
              value={message}
              onChange={(e) => {
                setMessage(e.target.value)
              }}
            />
            <div className="absolute -bottom-4 text-red text-[10px]">
              {formSubmitted && message.length <= 0 && (
                <div className="text-required_color">
                  {t('Please Enter Message')}
                </div>
              )}
            </div>
          </div>
          <FeatureFlagCaptcha ref={captchaRef} />
          {/* Submit Button */}
          <div className="">
            <Button
              variant="secondary"
              size="sx"
              onClick={() => {
                handleSubmit()
              }}
            >
              {t('SUBMIT')}
            </Button>
          </div>
        </div>
      </div>
      <div className="col-span-3 h-full shadow-[-2px_0_7px_-2px_rgba(0,0,0,0.14)]">
        <iframe
          loading="lazy"
          className="h-full w-full rounded-e-[5px]"
          src={`https://www.google.com/maps/embed/v1/view?key=AIzaSyDuSKvq6sOboFjL0FSIJ7qBLk-uqU-2dS4&center=${latitude},${longitude}&zoom=15`}
        ></iframe>
      </div>
    </div>
  )
}

export default ContactUsForm
